import { useMutation } from "react-query";
import { useQueryClient } from "react-query";
import { useBoolean } from "react-use";
import { t } from "@/i18n-js/instance";
import { marketingEmailTemplatesApi } from "@/react/api/marketingEmailTemplateApi";
import type { MarketingEmailTemplate } from "@/react/types/MarketingEmailTemplates";
import { useToast } from "@circle-react-uikit/ToastV2";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal";
import { getTemplateDetailQueryKey } from "./useTemplateDetail";
import { MARKETING_EMAIL_TEMPLATE_LIST_QUERY_KEY_PREFIX } from "./useTemplatesInfiniteQuery";

const localeNamespace = "settings.emails.templates";

export const useDeleteTemplate = ({
  template,
}: {
  template: MarketingEmailTemplate;
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isOpen, toggleIsOpen] = useBoolean(false);

  const { isLoading, mutate } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      marketingEmailTemplatesApi.deleteTemplate({ id }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries(["broadcasts", "show"]);
      void queryClient.invalidateQueries({
        queryKey: getTemplateDetailQueryKey(id),
      });
      void queryClient.invalidateQueries({
        queryKey: MARKETING_EMAIL_TEMPLATE_LIST_QUERY_KEY_PREFIX,
      });
      toast.success(t([localeNamespace, "delete_success"]));
    },
    onError: (error: Error) => {
      toast.error(error.message ?? t("settings.general.messages.error"));
    },
  });

  return {
    deleteModalElement: (
      <DeleteConfirmationModal
        isOpen={isOpen}
        onClose={toggleIsOpen}
        onConfirm={() => mutate({ id: template.id })}
        isLoading={isLoading}
        templateName={template.name}
      />
    ),
    openDeleteModal: toggleIsOpen,
  };
};
